import { configureStore } from '@reduxjs/toolkit';
import { adminConsoleMiddleware } from '../utils/adminConsole';
import { setupListeners } from '@reduxjs/toolkit/query';
import { persistStore, persistReducer } from 'redux-persist';
import { rehydratePermissions } from './rehydratePermissions';
import storage from 'redux-persist/lib/storage'; // Default storage is localStorage
import { combineReducers } from 'redux';
import { portalApi } from './api/portalApi';
import checkoutReducer from './slices/checkoutSlice';
import jobIdReducer from './slices/jobIdSlice';
import tableIdReducer from './slices/tableIdSlice';
import zoneCountReducer from './slices/zoneCountSlice';
import tbdReducer from './slices/tbdSlice';
import tbdApiSlice from './slices/tbdApiSlice';
import dropdownReducer from './slices/dropdownSlice';
import userReducer from './slices/userSlice';
import apiUrlsReducer from './slices/apiUrlSlice';
import sectionReducer from './slices/sectionSlice';
import psnReducer from './slices/psnSlice';
import apiQueueReducer from './slices/apiQueueSlice';
import jobDetailsReducer from './slices/jobDetailsSlice'; // Ensure this matches the slice name in jobDetailsSlice.js
import activeMechanicalTabReducer from './slices/activeMechanicalTabSlice';
import fetchJobDetailsReducer from './slices/fetchJobDetailsSlice';
import postChangeLogReducer from './slices/postChangeLogSlice';
import apiCallReducer from './slices/apiCallSlice';
import hasPumpApiPingReducer from './slices/hasPumpApiPing';
import activePumpsReducer from './slices/activePumpsSlice'; // Import the activePumps reducer
import activeFiltrationReducer from './slices/activeFiltrationSlice'; // Import the activeFiltration reducer
import changeOrderReducer from './slices/changeOrderSlice'; // Import the changeOrder reducer
import fieldDescriptionReducer from './slices/fieldDescriptionSlice'; // Import the fieldDescription reducer
import hasValuesReducer from './slices/hasValuesSlice'; // Import the hasValues reducer
import apiStatusReducer from './slices/apiStatusSlice'; // Import the apiStatus reducer
import globalVariablesReducer from './slices/globalVariablesSlice'; // Import the globalVariables reducer
import readOnlyReducer from "./slices/readOnlySlice"; // Import the readOnly reducer
import fieldNotifyTemplateReducer from "./slices/fieldNotifyTemplateSlice"; // Import the reducer
import jobScheduleReducer from './slices/jobScheduleSlice';
import weekDataReducer from './slices/weekDataSlice'; // Import the weekData reducer
import environmentReducer from './slices/environmentSlice'; // Import the environment reducer
import customRolesReducer from './slices/customRolesSlice'; // Import the customRoles reducer
import ptoReducer from './slices/ptoSlice'; // Import the PTO reducer
import holidaysReducer from './slices/holidaysSlice'; // Import the holidays reducer
import permissionsReducer from './slices/permissionsSlice'; // Import the permissions reducer
import pumpDetailsReducer from './slices/pumpDetailsSlice'; // Import the pumpDetails reducer
import sellPriceReducer from './slices/sellPriceSlice'; // Import the sellPrice reducer
import bookingsLogReducer from './slices/bookingsLogSlice'; // Import the bookingsLog reducer
import salesTargetsReducer from './slices/salesTargetsSlice'; // Import the salesTargets reducer
import npJobsReducer from './slices/npJobsSlice'; // Import the npJobs reducer
import longLeadReducer from './slices/longLeadSlice'; // Import the longLead reducer
import engineeringTasksReducer from './slices/engineeringTasksSlice'; // Import the engineeringTasks reducer
import criticalDataReducer from './slices/criticalDataSlice'; // Import the criticalData reducer
import protectedTimeReducer from './slices/protectedTimeSlice'; // Import the protectedTime reducer
import fetchStrategyReducer from './slices/fetchStrategySlice'; // Import fetch strategy reducer
import fullJobFetchReducer from './slices/fullJobFetchSlice'; // Import full job fetch reducer
import changeOrderDetailsReducer from './slices/changeOrderDetailsSlice'; // Import the changeOrderDetails reducer
import sharePointReducer from './slices/sharePointSlice'; // Import the sharePoint reducer

// ── Persist version ─────────────────────────────────────────────────────────
// Bump this number whenever the persisted slice whitelist changes so that
// existing users' stale / oversized storage is automatically cleared on
// their next page load rather than causing a QuotaExceededError.
const PERSIST_VERSION = 2;
const PERSIST_KEY = 'persist:root';

// ── Startup localStorage health check ────────────────────────────────────────
// Runs *before* the store is created so the persistor never attempts to
// rehydrate from bloated or version-mismatched data.
(function purgeStaleOrOversizedStorage() {
  try {
    const raw = localStorage.getItem(PERSIST_KEY);
    if (!raw) return;

    // Rough byte estimate (UTF-16 chars × 2).
    const estimatedBytes = raw.length * 2;
    const MAX_SAFE_BYTES = 256 * 1024; // 256 KB

    let storedVersion = null;
    try {
      const parsed = JSON.parse(raw);
      storedVersion = parsed?._persist ? JSON.parse(parsed._persist)?.version : null;
    } catch (_) { /* ignore parse errors – treat as stale */ }

    const isOversized = estimatedBytes > MAX_SAFE_BYTES;
    const isWrongVersion = storedVersion !== PERSIST_VERSION;

    if (isOversized || isWrongVersion) {
      // Remove all persist:* keys to get a completely clean slate.
      const keysToRemove = Object.keys(localStorage).filter(
        (k) => k.startsWith('persist:')
      );
      keysToRemove.forEach((k) => localStorage.removeItem(k));
      console.info(
        `[store] Cleared stale localStorage (oversized=${isOversized}, versionMismatch=${isWrongVersion}).`
      );
    }
  } catch (e) {
    // If anything goes wrong, do a best-effort clear so we don't block startup.
    try { localStorage.removeItem(PERSIST_KEY); } catch (_) {}
  }
}());

// ── Storage wrapper with write-failure self-healing ───────────────────────────
// If a setItem call raises QuotaExceededError the wrapper purges all
// persist:* keys and immediately retries the write with the (now-smaller)
// whitelisted payload.  This guards against any storage that was partially
// written after the whitelist change but before the browser quota was freed.
const safePurge = () => {
  try {
    const keysToRemove = Object.keys(localStorage).filter((k) =>
      k.startsWith('persist:')
    );
    keysToRemove.forEach((k) => localStorage.removeItem(k));
  } catch (_) {}
};

const storageWithFallback = {
  getItem: (...args) => storage.getItem(...args),
  removeItem: (...args) => storage.removeItem(...args),
  setItem: async (key, value) => {
    try {
      await storage.setItem(key, value);
    } catch (e) {
      const isQuota =
        e?.name === 'QuotaExceededError' ||
        e?.code === 22 ||
        e?.number === -2147024882;
      if (isQuota) {
        console.warn(
          '[store] localStorage quota exceeded – auto-purging stale persist data.'
        );
        safePurge();
        // Retry with a clean slate; if this also fails the catch below
        // swallows it so the app keeps running without persisted state.
        try {
          await storage.setItem(key, value);
        } catch (retryErr) {
          console.warn('[store] Write still failed after purge:', retryErr?.message);
        }
      }
    }
  },
};

// Define persist configuration
const persistConfig = {
  key: 'root',
  storage: storageWithFallback,
  // Only persist slices that are small and genuinely useful across sessions.
  whitelist: ['user', 'environment', 'customRoles', 'permissions', 'fetchStrategy'],
  version: PERSIST_VERSION,
  migrate: (state) => Promise.resolve(rehydratePermissions(state)),
};

// Combine reducers
const rootReducer = combineReducers({
  checkout: checkoutReducer,
  jobId: jobIdReducer,
  tables: tableIdReducer,
  zones: zoneCountReducer,
  tbd: tbdReducer,
  tbdApi: tbdApiSlice,
  dropdown: dropdownReducer,
  user: userReducer,
  apiUrls: apiUrlsReducer,
  sections: sectionReducer,
  psn: psnReducer,
  apiQueue: apiQueueReducer,
  jobDetails: jobDetailsReducer, // Ensure this matches the slice name in jobDetailsSlice.js
  activeMechanicalTab: activeMechanicalTabReducer,
  fetchJobDetails: fetchJobDetailsReducer,
  postChangeLog: postChangeLogReducer,
  apiCall: apiCallReducer,
  hasPumpApiPing: hasPumpApiPingReducer,
  activePumps: activePumpsReducer, // Add the activePumps reducer
  activeFiltration: activeFiltrationReducer, // Add the activeFiltration reducer
  changeOrder: changeOrderReducer, // Add the changeOrder reducer
  fieldDescriptions: fieldDescriptionReducer, // Add the fieldDescriptions reducer
  hasValues: hasValuesReducer, // Add the hasValues reducer
  apiStatus: apiStatusReducer, // Add the apiStatus reducer
  globalVariables: globalVariablesReducer, // Add the globalVariables reducer
  readOnly: readOnlyReducer, // Add the readOnly reducer
  fieldNotifyTemplate: fieldNotifyTemplateReducer, // Add the fieldNotifyTemplate reducer
  jobSchedule: jobScheduleReducer, // Add the jobSchedule reducer
  weekData: weekDataReducer, // Add the weekData reducer
  environment: environmentReducer, // Add the environment reducer
  customRoles: customRolesReducer, // Add the customRoles reducer
  pto: ptoReducer, // Add the PTO reducer
  holidays: holidaysReducer, // Add the holidays reducer
  permissions: permissionsReducer, // Add the permissions reducer
  pumpDetails: pumpDetailsReducer, // Add the pumpDetails reducer
  sellPrice: sellPriceReducer, // Add the sellPrice reducer
  bookingsLog: bookingsLogReducer, // Add the bookingsLog reducer
  salesTargets: salesTargetsReducer, // Add the salesTargets reducer
  npJobs: npJobsReducer, // Add the npJobs reducer
  longLead: longLeadReducer, // Add the longLead reducer
  engineeringTasks: engineeringTasksReducer, // Add the engineeringTasks reducer
  criticalData: criticalDataReducer, // Add the criticalData reducer
  protectedTime: protectedTimeReducer, // Add the protectedTime reducer
  fetchStrategy: fetchStrategyReducer, // Add fetch strategy reducer
  fullJobFetch: fullJobFetchReducer, // Add full-job fetch reducer
  changeOrderDetailsGlobal: changeOrderDetailsReducer, // Add the changeOrderDetails reducer
  sharePoint: sharePointReducer, // Add the sharePoint reducer
  // RTK Query API reducer – manages server cache (excluded from redux-persist)
  [portalApi.reducerPath]: portalApi.reducer,
});

// Apply persistReducer to the rootReducer
const persistedReducer = persistReducer(persistConfig, rootReducer);

const isProduction = process.env.NODE_ENV === 'production';
const enableReduxDevChecks = process.env.REACT_APP_ENABLE_REDUX_DEV_CHECKS === 'true';

// Create store with persisted reducer and thunk middleware
const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      immutableCheck: isProduction || enableReduxDevChecks
        ? {
            warnAfter: 128,
            ignoredPaths: [
              'jobDetails.jobDetails',
              'portalApi.queries',
              'portalApi.mutations',
            ],
          }
        : false,
      serializableCheck: isProduction || enableReduxDevChecks
        ? {
            // Ignore these action types
            ignoredActions: ['persist/PERSIST', 'persist/REHYDRATE'],
            // Ignore these field paths in all actions
            ignoredActionPaths: ['meta.arg', 'payload.timestamp'],
            // Ignore these paths in the state
            ignoredPaths: ['register', 'jobDetails.jobDetails'],
            warnAfter: 128,
          }
        : false,
    })
      .prepend(adminConsoleMiddleware)
      .concat(portalApi.middleware),
  devTools: !isProduction,
});

// Create persistor
const persistor = persistStore(store);

// Enable automatic refetch behaviors for refetchOnFocus/refetchOnReconnect,
// but throttle focus/reconnect signals to avoid repeated reloads when users
// switch tabs frequently.
const REFETCH_THROTTLE_MS = 15 * 60 * 1000;

setupListeners(store.dispatch, (dispatch, actions) => {
  let lastRefetchSignalAt = 0;

  const canSignalRefetch = () => Date.now() - lastRefetchSignalAt >= REFETCH_THROTTLE_MS;
  const markSignal = () => {
    lastRefetchSignalAt = Date.now();
  };

  const handleFocus = () => {
    if (!canSignalRefetch()) return;
    markSignal();
    dispatch(actions.onFocus());
  };

  const handleOnline = () => {
    if (!canSignalRefetch()) return;
    markSignal();
    dispatch(actions.onOnline());
  };

  const handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      handleFocus();
    }
  };

  const handleFocusLost = () => dispatch(actions.onFocusLost());
  const handleOffline = () => dispatch(actions.onOffline());

  if (typeof window !== 'undefined' && window.addEventListener) {
    window.addEventListener('focus', handleFocus, false);
    window.addEventListener('online', handleOnline, false);
    window.addEventListener('offline', handleOffline, false);
    window.addEventListener('blur', handleFocusLost, false);
    document.addEventListener('visibilitychange', handleVisibilityChange, false);
  }

  return () => {
    window.removeEventListener('focus', handleFocus);
    window.removeEventListener('online', handleOnline);
    window.removeEventListener('offline', handleOffline);
    window.removeEventListener('blur', handleFocusLost);
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  };
});

// Export both store and persistor
export { store, persistor };
